import {
	Component,
	forwardRef,
	EventEmitter,
	Output,
	Input,
} from '@angular/core';
import { CustomerLink, RailroadLink } from 'src/shared/api';
import {
	displayName,
	idComparer,
	touchControl,
} from 'src/shared/utilities/utility-functions';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import {
	ControlValueAccessor,
	FormControl,
	NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
	takeUntil,
	map,
	filter,
	distinctUntilChanged,
	skipWhile,
	first,
} from 'rxjs/operators';
import { LookUpsService } from 'src/shared/lookups.service';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

@Component({
	selector: 'rpshared-railroad-link',
	templateUrl: './railroad-link.component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => RailroadLinkComponent),
			multi: true,
		},
	],
})
export class RailroadLinkComponent extends SelfDestruct
	implements ControlValueAccessor {
	private _required = false;
	@Input()
	get required(): boolean {
		return this._required;
	}
	set required(value: boolean) {
		this._required = coerceBooleanProperty(value);
	}

	private _readonly = false;
	@Input()
	get readonly(): boolean {
		return this._readonly;
	}
	set readonly(value: boolean) {
		this._readonly = coerceBooleanProperty(value);
	}

	@Output() optionSelected = new EventEmitter<RailroadLink>();

	railroad = new FormControl('') as FormControl;
	initialValue: RailroadLink;

	readonly railroads$ = this.lookUpsService.railroads;
	readonly displayName = displayName;
	readonly idComparer = idComparer;

	get railroadName(): string {
		return this.railroadEntity ? this.railroadEntity.name : '';
	}

	get railroadEntity(): RailroadLink {
		return this.railroad.value as RailroadLink;
	}

	constructor(private readonly lookUpsService: LookUpsService) {
		super();
	}

	// model --> view
	writeValue(value: RailroadLink | number): void {
		if (typeof value === 'number') {
			this.lookUpsService.railroads
				.pipe(
					first(),
					map((railroads) => railroads.find((rr) => rr.id === value))
				)
				.subscribe((railroad) => {
					this.initialValue = railroad;
					this.railroad.setValue(railroad, { emitEvent: false });
				});
		} else if (value) {
			this.initialValue = value;
			this.railroad.setValue(value, { emitEvent: false });
		} else {
			this.railroad.reset('', { emitEvent: false });
		}
	}

	// view --> model
	registerOnChange(fn: (value: RailroadLink | number) => void) {
		this.railroad.valueChanges
			.pipe(takeUntil(this.destroyed))
			.subscribe(fn);
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = touchControl(this.railroad)(fn);
	}

	onTouched: () => void = () => {};
}

@Component({
	selector: 'rpshared-railroad-id',
	templateUrl: './railroad-link.component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => RailroadIdComponent),
			multi: true,
		},
	],
})
export class RailroadIdComponent extends RailroadLinkComponent {
	registerOnChange(fn: (value: CustomerLink | number) => void) {
		this.railroad.valueChanges
			.pipe(
				filter((v) => !v || typeof v !== 'string'),
				map((v) => (v ? v.id : null)),
				distinctUntilChanged(),
				skipWhile<number | null>((v) => !v && !this.initialValue),
				takeUntil(this.destroyed)
			)
			.subscribe(fn);
	}
}
